import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../../commonComponents/translate';
import AppProductProp from '../../propTypes/appProductProp';
import Modal from '../../../../commonComponents/modal/Modal';
import ModalHeader from '../../../../commonComponents/modal/ModalHeader';
import FormAddressFragment from '../../../../commonComponents/forms/Address/FormAddressFragment';
import AddressProp from '../../../../commonComponents/forms/Address/AddressProp';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import CreditCardSelector from '../forms/payment/creditCardSelector/CreditCardSelector';
import StripeCardProp from '../../propTypes/StripeCardProp';
import ModalFooter from '../../../../commonComponents/modal/ModalFooter';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import withCSRF from '../../../../commonComponents/withCSRF';
import {
  checkStripeKeys,
  compose,
  displayMoney,
  IN_APP_PRODUCT_TYPE,
} from '../../../../services/utilityFunctions';
import { useCards } from '../../../../services/customHooks';
import StripeService from '../../../../services/stripeService';
import Notifications from '../../../../services/notifications';
import Request from '../../../../services/request';
import DiscountInput from '../../../shared/DiscountInput';

function PrintModal(props) {
  const { loggedUser } = useContext(LoggedUserContext);

  const {
    t,
    _csrf,
    certificateId,
    appProduct,
    countries,
    cards: propCards,
    onCardsChange,
    onClose,
  } = props;
  const { productType, userCurrency = {}, product } = appProduct;
  const hasPrice = userCurrency.total;

  const { cards, handleAddCard, handleRemoveCard, handleSelectCard } = useCards(
    _csrf,
    'credit-card-container',
    propCards,
    onCardsChange,
  );

  const [cardError, setCardError] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(null);
  useEffect(
    () => {
      if (!coupon) {
        setDiscountAmount(null);
      } else {
        new Request()
          .get(
            `/discount/${coupon.code}/calculate?product=${product}&price=${
              userCurrency.price
            }`,
          )
          .done(({ data: amount }) => {
            setDiscountAmount(amount);
          })
          .fail(err => {
            setDiscountAmount(null);
            Notifications.showErrorFromRequest(err);
          });
      }
    },
    [coupon],
  );

  const subTotal = userCurrency.price - discountAmount;
  const taxAmount = subTotal * (userCurrency.tax / 100);
  const priceItems = [
    { label: t('print_modal_price'), value: userCurrency.price },
    { label: t('print_modal_tax'), value: taxAmount },
    { label: t('print_modal_total'), value: subTotal + taxAmount },
  ];
  if (discountAmount !== null) {
    priceItems.splice(1, 0, {
      label: t('print_modal_discount'),
      value: discountAmount * -1,
    });
  }

  const [address, setAddress] = useState(
    loggedUser.address || AddressProp.defaultProp,
  );

  const handleFieldChange = field => v =>
    setAddress(a => ({ ...a, [field]: v }));

  const performPayment = async cardId =>
    StripeService.initiatePayment(_csrf, {
      product: appProduct.product,
      quantity: 1,
      currency: userCurrency.code,
      amount: userCurrency.price,
      paymentMethod: cardId,
      customerId: loggedUser.stripe.customerId,
      coupon: coupon ? coupon._id : undefined,
    });

  const performOrder = ({ amount, currency }) => {
    const params = {
      amount,
      currency,
      shipping: {
        recipientName: loggedUser.username,
        email: loggedUser.email,
        phone: loggedUser.phone,
        line1: address ? address.address_1 : '',
        line2: address ? address.address_2 : '',
        city: address ? address.city : '',
        postCode: address ? address.zipCode : '',
        countryCode: address ? address.countryCode : '',
      },
    };

    const url =
      productType === IN_APP_PRODUCT_TYPE.POSTER
        ? '/profile/poster/print'
        : `/certificate/${certificateId}/print`;

    new Request(_csrf)
      .post(url, params)
      .done(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('print_order_sent'),
        );
        onClose();
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_printOrder');
      });
  };

  const handleSubmit = async () => {
    try {
      const card = cards.find(c => c.isDefault);

      if (!card) {
        setCardError(true);
        return null;
      }

      if (checkStripeKeys(loggedUser)) {
        Notifications.showNotificationError(
          t('error'),
          t('ERROR_MISSING_CUSTOMER'),
        );
        return null;
      }
      if (!address || !address.countryCode) {
        Notifications.showNotificationError(
          t('error'),
          t('error_missingCountry'),
        );
        return null;
      }

      if (
        !appProduct.userCurrency ||
        !appProduct.userCurrency.price ||
        !appProduct.userCurrency.code
      ) {
        Notifications.showNotificationError(
          t('error'),
          t('error_missingUserCurrency'),
        );
        return null;
      }

      const result = await performPayment(card.id);
      performOrder(result);

      return null;
    } catch (err) {
      Notifications.showNotificationError(t('error'), t(err));
      return null;
    }
  };

  return (
    <Modal onClose={onClose} tabIndex={null}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <ModalHeader
            title={t(`print_modal_${productType}`)}
            onClose={onClose}
          />
          <div
            id="modalBody"
            className="modal-body"
            style={{ maxHeight: '70vh', overflowY: 'auto' }}
          >
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="print-details-content">
                    <div>
                      <h4>{t(`print_modal_${productType}`)}</h4>
                      <p className="print_format">
                        {t(`print_modal_${productType}_format`)}
                      </p>
                      <p className="description">
                        {t(`print_modal_${productType}_description`)}
                      </p>
                      <br />
                      {hasPrice ? (
                        <div className="price-items-container">
                          {priceItems.map(i => (
                            <div className="price-item" key={i.label}>
                              <span className="price-label">{i.label}</span>
                              <span className="price-value">
                                {displayMoney(i.value, userCurrency.symbol)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="price">{t('print_modal_noPrice')}</p>
                      )}
                    </div>
                    <br />
                    <DiscountInput onChange={setCoupon} />
                    <br />
                    <FormAddressFragment
                      address={address}
                      onFieldChange={handleFieldChange}
                      countries={countries}
                    />
                    <br />
                    <div id="credit-card-container">
                      <CreditCardSelector
                        cards={cards}
                        onSelectCard={handleSelectCard}
                        hasError={cardError}
                        isModeSelection
                        onAddCard={handleAddCard}
                        onRemoveCard={handleRemoveCard}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ModalFooter
            saveIsLoader
            onCancel={onClose}
            onSave={handleSubmit}
            cancelText={t('cancel')}
            saveText={
              <>
                <i
                  className="la la-shopping-cart"
                  style={{ marginRight: '5px', marginBottom: '5px' }}
                />
                {t('print_modal_orderPrint')}
              </>
            }
          />
        </div>
      </div>
    </Modal>
  );
}

PrintModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  certificateId: PropTypes.string,
  appProduct: PropTypes.shape(AppProductProp.propType).isRequired,
  onClose: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  onCardsChange: PropTypes.func,
};

PrintModal.defaultProps = {
  onCardsChange: null,
  certificateId: null,
};

export default compose(
  translate,
  withCSRF,
)(PrintModal);
